import uniq from 'utils/uniq';
import {
  isListOfColoredDisplayProducts,
  getColoredDisplayProductIds,
  getRelatedProductsIds,
} from 'data/lib/product';
import productReducer from 'data/lib/productReducer';

// Pure picker state derivation shared by ProductConfiguration and the product pages. Everything
// here runs during render on the server and the client, so inputs must be props or data context
// only (no window, storage, dates or randomness).

// Reducer products derive the SKU from the reducer and have no primary product list
export const getPrimaryProducts = (product, dataContext) => {
  if (product.reducer) {
    return [];
  }

  const primaryProductIds = product.options.flatMap(option => option.products.primary);

  return uniq(primaryProductIds)
    .map(dataContext.getSkuProduct)
    .filter(skuProduct => !!skuProduct)
    .sort((a, b) => a.categoryPosition - b.categoryPosition);
};

export const getInitialConfiguration = (product, primaryProducts, defaultSecondaryIndex) => {
  const config = {};
  if (product.reducer) {
    productReducer(product.reducer, product.options).forEach(({ key, values }) => {
      config[key] = values[0];
    });
  } else {
    config.primaryIndex = Math.max(
      0,
      primaryProducts.findIndex(skuProduct => skuProduct.id === product.config?.defaultSku),
    );
  }

  config.secondaryIndex = defaultSecondaryIndex;

  return config;
};

// Bundles offered for the selected SKU, limited to ones with at least one related secondary SKU
export const getSecondaryProductOptions = (product, skuProduct, dataContext) => {
  if (!skuProduct) {
    return [];
  }

  const relatedProductIds = getRelatedProductsIds(skuProduct);
  const bundleIds = product.bundles
    .filter(bundle => bundle.products.primary.indexOf(skuProduct.id) >= 0)
    .map(bundle => bundle.id);

  return uniq(bundleIds)
    .map(dataContext.getSkuProduct)
    .filter(bundle => !!bundle && bundle.products.secondary.length > 0)
    .filter(
      bundle =>
        dataContext.getSkuProducts(
          bundle.products.secondary.filter(id => relatedProductIds.indexOf(id) >= 0),
        ).length > 0,
    )
    .sort((a, b) => a.categoryPosition - b.categoryPosition);
};

export const computePriceData = (
  product,
  primaryProducts,
  secondaryProductOptions,
  upsellOptions,
  configuration,
) => {
  let primaryProduct;

  const data = {};
  if (product.reducer) {
    const value = productReducer(product.reducer, product.options, configuration);
    primaryProduct = value;
    let config = configuration;
    if (Array.isArray(value)) {
      primaryProduct = value[0];
      config = value[1];
    }
    data.config = config;
  } else {
    primaryProduct = primaryProducts[configuration.primaryIndex];
    data.config = configuration;
    if (configuration.primaryData && Object.keys(configuration.primaryData).length > 0) {
      data.primaryProductData = {
        options: configuration.primaryData,
      };
    }
  }

  // Products without any sellable SKU render no picker, pages then keep an empty configuration
  if (!primaryProduct) {
    return {};
  }

  data.productId = primaryProduct.id;
  data.product = primaryProduct;

  let quantity = configuration.quantity ?? 1;
  let price = primaryProduct.price;

  if (primaryProduct.priceTiers) {
    const defaultTier = Object.keys(primaryProduct.priceTiers)[0];
    const currentTierPrice = primaryProduct.priceTiers[`${quantity}`];

    if (currentTierPrice != null) {
      price = currentTierPrice;
    } else if (defaultTier != null) {
      quantity = parseInt(defaultTier, 10);
      price = primaryProduct.priceTiers[defaultTier];
    }
  }

  data.productPrice = price;
  data.quantity = quantity;
  data.price = price;

  // BUNDLES
  if (
    configuration.secondaryIndex >= 0 &&
    secondaryProductOptions.length > configuration.secondaryIndex
  ) {
    data.bundle = secondaryProductOptions[configuration.secondaryIndex];
    data.secondaryProductIds = data.bundle.products.secondary;
    data.price = data.bundle.price;
    if (configuration.secondaryData && Object.keys(configuration.secondaryData).length > 0) {
      if (isListOfColoredDisplayProducts(data.bundle.products.secondary)) {
        // filter by options, and there are no data - new approach
        data.secondaryProductIds = getColoredDisplayProductIds(
          data.bundle.products.secondary,
          configuration.secondaryData,
        );
        data.secondaryProductData = undefined;
      } else {
        // dont't filter and options are represented as data - old approach
        data.secondaryProductData = {
          options: configuration.secondaryData,
        };
      }
    }
  }

  // UPSELL
  if (
    configuration.secondaryIndex >= 0 &&
    upsellOptions.length > configuration.secondaryIndex
  ) {
    const upsellOption = upsellOptions[configuration.secondaryIndex];

    if (configuration.secondaryData && Object.keys(configuration.secondaryData).length > 0) {
      if (isListOfColoredDisplayProducts(upsellOption.skus)) {
        // filter by options, and there are no data - new approach
        const upsellSkuProduct = upsellOption.skuProducts.find(
          p =>
            getColoredDisplayProductIds(upsellOption.skus, configuration.secondaryData).indexOf(
              p.id,
            ) >= 0,
        );
        if (upsellSkuProduct) {
          data.upsell = upsellSkuProduct;
          data.price += upsellSkuProduct.price;
          data.secondaryProductData = undefined;
        }
      } else {
        // dont't filter and options are represented as data - old approach
        const upsellSkuProduct = upsellOption.skuProducts[0];
        data.upsell = upsellSkuProduct;
        data.price += upsellSkuProduct.price;
        data.secondaryProductData = {
          options: configuration.secondaryData,
        };
      }
    }
  }

  return data;
};

// SKU a configuration selects: the reducer's match, or the primary product at primaryIndex
export const resolvePrimaryProduct = (product, primaryProducts, configuration) => {
  if (!product.reducer) {
    return primaryProducts[configuration.primaryIndex];
  }

  const value = productReducer(product.reducer, product.options, configuration);
  return Array.isArray(value) ? value[0] : value;
};

// What the picker reports through onChange on mount, so pages can render the price before it.
// Bundles and upsells come from the SKU the configuration selects, which is data.product.
export const getInitialPriceData = (product, dataContext, defaultPrintOnly) => {
  const primaryProducts = getPrimaryProducts(product, dataContext);
  const configuration = getInitialConfiguration(
    product,
    primaryProducts,
    defaultPrintOnly ? -1 : 0,
  );
  const skuProduct = resolvePrimaryProduct(product, primaryProducts, configuration);

  return computePriceData(
    product,
    primaryProducts,
    getSecondaryProductOptions(product, skuProduct, dataContext),
    skuProduct ? product.upsell : [],
    configuration,
  );
};
